import { useAuthStore } from '@/store'
import type { AuthResponse } from '@/types'
import type { Role } from '@/types/role'
import { minRoleForPath, roleMeetsMinRole } from './roleAccess'
import { isPhoneUserAgent } from './phoneUa'
import { isPublicAuthPath, isSafeInternalPath, shouldBootstrapStoredSession } from './publicPaths'
import { isFaceIdClient } from './deviceUnlock'
import { rememberLandscapePath } from './talkWelt'

export { isPublicAuthPath, isSafeInternalPath, shouldBootstrapStoredSession }

const PENDING_LOGIN_NEXT_KEY = 'pending_login_next'
const PENDING_SUB_KEY = 'pending_substitution_redirect'
const PENDING_PREP_KEY = 'pending_prep_redirect'

export function toInternalPath(url: string | null | undefined, fallback = '/dashboard'): string {
  if (!url) return fallback
  try {
    const target = new URL(url, 'http://local.invalid')
    const path = `${target.pathname}${target.search}${target.hash}`
    return isSafeInternalPath(path) ? path : fallback
  } catch {
    return isSafeInternalPath(url) ? url : fallback
  }
}

export function storePendingLoginNext(path: string): void {
  const internal = toInternalPath(path, '')
  if (internal && isSafeInternalPath(internal)) {
    localStorage.setItem(PENDING_LOGIN_NEXT_KEY, internal)
  }
}

export function consumePendingLoginNext(): string | null {
  const value = localStorage.getItem(PENDING_LOGIN_NEXT_KEY)
  if (value) localStorage.removeItem(PENDING_LOGIN_NEXT_KEY)
  if (value && isSafeInternalPath(value)) return value
  return null
}

export function isPathAllowedForRole(role: Role, path: string): boolean {
  const internal = toInternalPath(path, '')
  if (!internal || !isSafeInternalPath(internal)) return false
  const pathname = internal.split('?')[0] ?? internal
  if (isPublicAuthPath(pathname)) return false
  const scopes = role === 'engineer' ? (useAuthStore.getState().user?.engineer_scopes ?? []) : undefined
  return roleMeetsMinRole(role, minRoleForPath(pathname), pathname, scopes)
}

function consumeJsonRequestId(key: string): string | null {
  const raw = localStorage.getItem(key)
  if (!raw) return null
  localStorage.removeItem(key)
  try {
    const { request_id } = JSON.parse(raw) as { request_id?: unknown }
    return request_id ? String(request_id) : null
  } catch {
    return null
  }
}

function maybeTalk(dest: string): string {
  if (typeof navigator !== 'undefined' && isPhoneUserAgent(navigator.userAgent)) {
    if (useAuthStore.getState().user?.talk_enabled !== false) {
      rememberLandscapePath(dest)
      return '/talk'
    }
  }
  return dest
}

export function defaultHomePath(role: Role | undefined): string {
  return maybeTalk(role === 'engineer' ? '/messages' : '/dashboard')
}

export function resolvePostLoginPath(role: Role | undefined): string {
  const subId = consumeJsonRequestId(PENDING_SUB_KEY)
  if (subId) {
    const path = `/teacher/incoming-requests?highlight=${subId}&focus=1`
    if (role && isPathAllowedForRole(role, path)) return maybeTalk(path)
  }
  const prepId = consumeJsonRequestId(PENDING_PREP_KEY)
  if (prepId) {
    const path = `/teacher/request/${prepId}/prep?from=incoming`
    if (role && isPathAllowedForRole(role, path)) return maybeTalk(path)
  }
  const pendingNext = consumePendingLoginNext()
  if (pendingNext && role && isPathAllowedForRole(role, pendingNext)) return maybeTalk(pendingNext)
  return maybeTalk(role === 'engineer' ? '/messages' : '/dashboard')
}

export function clearPendingLoginNext(): void {
  localStorage.removeItem(PENDING_LOGIN_NEXT_KEY)
}

export async function applyMagicSession(response: {
  access_token?: string
  refresh_token?: string
  user?: unknown
  token_type?: string
  expires_in?: number
}): Promise<boolean> {
  if (!response.access_token || !response.refresh_token || !response.user) return false
  await useAuthStore.getState().completeSession({
    access_token: response.access_token,
    refresh_token: response.refresh_token,
    token_type: 'bearer',
    expires_in: response.expires_in ?? 0,
    user: response.user as AuthResponse['user'],
  }, { enroll: isFaceIdClient() })
  return true
}
